import type { RouteRecordRaw } from "vue-router";

export const protectedRoutes: RouteRecordRaw[] = [
  {
    // Top-level Goals hub (tabbed: My Goals / Company Goals / Team)
    path: "/goals",
    name: "goals",
    component: () => import("@/views/GoalsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Career Frameworks (Rubrics) — Surface A: view-only read view (Pando-style)
    path: "/rubrics",
    name: "rubrics",
    component: () => import("@/views/RubricViewPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Surface B: admin rubric editor
    path: "/rubrics/:uuid/edit",
    name: "rubric-editor",
    component: () => import("@/views/RubricEditorPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Rubrics Slice 2: competency calibration detail page (self vs manager)
    path: "/calibration/:assessmentUuid",
    name: "competency-calibration",
    component: () => import("@/views/CompetencyAssessmentPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Story 5.1: Admin Signup & Company Profile Creation
    path: "/onboarding",
    name: "onboarding",
    component: () => import("@/views/admin/OnboardingPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    // Redirect legacy /dashboard URL
    path: "/dashboard",
    redirect: "/chat",
  },
  // ── Dev pages (Clerk org:admin only — Jordan, Adam, Barb) ──
  {
    path: "/dev/team-dashboard",
    name: "manager-dashboard",
    component: () => import("@/views/ManagerDashboardPage.vue"),
    meta: { requiresAuth: true, requiresClerkAdmin: true },
  },
  {
    // Story 4.3: Company Organizational Dashboard ("Insights" in nav, admin-only)
    path: "/dashboard/company",
    name: "company-dashboard",
    component: () => import("@/views/CompanyDashboardPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Redirect old 1:1 prep URL to new EmployeeActivityPage with 1:1 tab
    path: "/dashboard/team/:employeeUuid/prep",
    redirect: (to) =>
      `/activity/employee/${to.params.employeeUuid}?tab=oneone_prep`,
  },
  // ── Unified Activity Pages ──
  {
    path: "/activity",
    name: "my-activity",
    component: () => import("@/views/MyActivityPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/activity/employee/:id/review",
    name: "employee-review-draft",
    component: () => import("@/views/manager/ReviewDraftPage.vue"),
    meta: { requiresAuth: true, requiresRole: "manager" },
    props: true,
  },
  {
    path: "/activity/employee/:id/briefing/:briefingId",
    name: "briefing-detail",
    component: () => import("@/views/manager/BriefingDetailPage.vue"),
    meta: { requiresAuth: true, requiresRole: "manager" },
    props: true,
  },
  {
    path: "/activity/employee/:id",
    name: "employee-activity",
    component: () => import("@/views/EmployeeActivityPage.vue"),
    meta: { requiresAuth: true, requiresRole: "manager" },
    props: true,
  },
  // ── Backwards compatibility redirects (self-view) ──
  { path: "/dashboard/me", redirect: "/activity" },
  {
    path: "/dashboard/employee/:id",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  { path: "/activity/feedback-received", redirect: "/activity" },
  { path: "/activity/feedback-given", redirect: "/activity" },
  { path: "/activity/requests", redirect: "/activity" },
  { path: "/activity/achievements", redirect: "/activity" },
  { path: "/activity/recognitions", redirect: "/activity" },
  // /activity/pulse remains pointing at /activity for v1; the unified
  // activity hub already surfaces pulse history via the GET /api/activity/pulse
  // endpoint (now unioned across system + creator-authored sources).
  { path: "/activity/pulse", redirect: "/activity" },
  // ── Multi-Source Pulses (creator hub) — admins or users with direct reports ──
  {
    path: "/pulses",
    name: "pulses-list",
    component: () => import("@/views/PulsesListPage.vue"),
    meta: { requiresAuth: true, requiresRole: "managerOrAdmin" },
  },
  {
    path: "/pulses/new",
    name: "pulses-new",
    component: () => import("@/views/CreatePulsePage.vue"),
    meta: { requiresAuth: true, requiresRole: "managerOrAdmin" },
  },
  {
    path: "/pulses/:id",
    name: "pulses-detail",
    component: () => import("@/views/PulseDetailPage.vue"),
    meta: { requiresAuth: true, requiresRole: "managerOrAdmin" },
    props: true,
  },
  {
    path: "/pulses/:id/results",
    name: "pulses-results",
    component: () => import("@/views/PulseResultsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "managerOrAdmin" },
    props: true,
  },
  {
    path: "/pulses/template/:id/results",
    name: "pulse-template-results",
    component: () => import("@/views/PulseTemplateResultsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
    props: true,
  },
  // ── Backwards compatibility redirects (manager drill-down) ──
  {
    path: "/activity/employee/:id/feedback-received",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    path: "/activity/employee/:id/feedback-given",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    path: "/activity/employee/:id/requests",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    path: "/activity/employee/:id/achievements",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    path: "/activity/employee/:id/recognitions",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    path: "/activity/employee/:id/pulse",
    redirect: (to) => `/activity/employee/${to.params.id}`,
  },
  {
    // Dev Admin Tools
    path: "/dev-admin",
    name: "dev-admin",
    component: () => import("@/views/admin/DevAdminPage.vue"),
    meta: { requiresAuth: true, requiresClerkAdmin: true },
  },
  {
    path: "/dev-admin/pando-connections",
    name: "dev-admin-pando-connections",
    component: () => import("@/views/admin/PandoConnectionsDevPage.vue"),
    meta: { requiresAuth: true, requiresClerkAdmin: true },
  },
  // ── Regular user pages ──
  {
    path: "/chat",
    name: "chat",
    component: () => import("@/views/ConversationPage.vue"),
    meta: { requiresAuth: true },
  },
  // ── Manager pages (in-app role) ──
  {
    // My Team hub (tabbed: Insights, Digest, People)
    path: "/dashboard/team",
    name: "my-team",
    component: () => import("@/views/manager/WeeklyDigestPage.vue"),
    meta: { requiresAuth: true, requiresRole: "manager" },
  },
  // Backwards compat redirect
  { path: "/dashboard/team/weekly-digest", redirect: "/dashboard/team" },
  {
    // Manager Channel Analysis
    path: "/dashboard/team/channel-analysis",
    name: "manager-channel-analysis",
    component: () => import("@/views/manager/ManagerChannelAnalysisPage.vue"),
    meta: { requiresAuth: true, requiresRole: "manager" },
  },
  // ── Settings pages (in-app admin role) ──
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/SettingsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/hierarchy",
    name: "admin-hierarchy",
    component: () => import("@/views/HierarchyManagerPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    path: "/admin/teams",
    name: "admin-teams",
    component: () => import("@/views/admin/TeamsAndDepartmentsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/integrations",
    name: "admin-integrations",
    component: () => import("@/views/admin/IntegrationsView.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    path: "/dev/channel-insights",
    name: "dev-channel-insights",
    component: () =>
      import("@/views/admin/integrations/SlackChannelInsights.vue"),
    meta: { requiresAuth: true, requiresClerkAdmin: true },
  },
  {
    path: "/admin/settings",
    name: "admin-settings",
    component: () => import("@/views/admin/AdminSettingsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Surface C: assign a rubric framework + track + level to people (bulk).
    path: "/admin/leveling",
    name: "admin-leveling",
    component: () => import("@/views/admin/LevelingAssignmentsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Rubrics Slice 2: edit the company-standard competency rating scale (R7)
    path: "/admin/rating-scale",
    name: "admin-rating-scale",
    component: () => import("@/views/admin/RatingScalePage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Admin bulk performance reviews: generate review drafts for any employees
    path: "/admin/performance-reviews",
    name: "admin-performance-reviews",
    component: () => import("@/views/admin/PerformanceReviewsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Admin Invitation Page: Post-onboarding invite management
    path: "/admin/invite",
    name: "admin-invite",
    component: () => import("@/views/admin/InviteTeamPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    path: "/admin/pulse-questions",
    redirect: "/pulses?tab=template",
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    // Tech-Spec: Stripe Self-Serve Billing — Task 25
    path: "/admin/billing",
    name: "admin-billing",
    component: () => import("@/views/admin/BillingPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    path: "/admin/organizational-context",
    name: "admin-org-context",
    component: () => import("@/views/admin/OrgContextView.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  {
    path: "/admin/performance-review-settings",
    name: "admin-performance-review-settings",
    component: () => import("@/views/admin/PerformanceReviewSettingsPage.vue"),
    meta: { requiresAuth: true, requiresRole: "admin" },
  },
  // Legacy redirect — the V1 rename moved this page to /admin/organizational-context.
  { path: "/admin/rubrics", redirect: "/admin/organizational-context" },
];
